import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { OverflowList } from '@theme/overflow-list';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets - The fieldset elements.
 * @property {HTMLElement} [overflowList] - The overflow list element.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  /** @type {boolean} */
  #requiresExplicitSelection = false;

  /** @type {string | undefined} */
  #explicitSelectionOptionId;

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  #onChange = (event) => this.variantChanged(event);

  connectedCallback() {
    super.connectedCallback();
    const preUpgradeSelection = this.capturePreUpgradeExplicitSelection();
    this.resetExplicitSelectionContract();

    this.addEventListener('change', this.#onChange);
    this.replayPreUpgradeExplicitSelection(preUpgradeSelection);
    this.#resizeObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.removeEventListener('change', this.#onChange);
    this.#resizeObserver.disconnect();
  }

  /**
   * Captures a real size choice made while the server-rendered picker was
   * waiting for this custom element to upgrade. Required-size markup starts
   * deliberately unselected, so a non-placeholder value here can only have
   * come from the customer.
   * @returns {{ optionValueId: string, kind: 'radio' | 'select' } | undefined}
   */
  capturePreUpgradeExplicitSelection() {
    if (this.dataset.requireExplicitSelection !== 'true') return undefined;

    for (const field of this.querySelectorAll('[data-explicit-selection-field]')) {
      if (field instanceof HTMLFieldSetElement) {
        const radio = field.querySelector('input[type="radio"][data-option-value-id]:checked');
        if (
          radio instanceof HTMLInputElement &&
          radio.dataset.currentChecked !== 'true' &&
          radio.dataset.optionValueId
        ) {
          return { optionValueId: radio.dataset.optionValueId, kind: 'radio' };
        }
      } else if (field instanceof HTMLSelectElement) {
        const option = field.options[field.selectedIndex];
        if (option?.value && !option.disabled && option.dataset.optionValueId) {
          return { optionValueId: option.dataset.optionValueId, kind: 'select' };
        }
      }
    }

    return undefined;
  }

  /**
   * Replays a pre-upgrade choice through the normal request lifecycle after
   * the required-size lock and the stable change listener are installed.
   * @param {{ optionValueId: string, kind: 'radio' | 'select' } | undefined} selection
   */
  replayPreUpgradeExplicitSelection(selection) {
    if (!selection) return;

    queueMicrotask(() => {
      if (!this.isConnected || this.dataset.requireExplicitSelection !== 'true') return;

      const option = this.querySelector(
        `[data-explicit-selection-field] [data-option-value-id="${CSS.escape(selection.optionValueId)}"]`
      );
      if (selection.kind === 'radio' && option instanceof HTMLInputElement) {
        option.checked = true;
        option.dispatchEvent(new Event('change', { bubbles: true }));
      } else if (
        selection.kind === 'select' &&
        option instanceof HTMLOptionElement &&
        option.parentElement instanceof HTMLSelectElement
      ) {
        option.parentElement.value = option.value;
        option.parentElement.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  /**
   * Resets private selection state after a server-rendered product surface is
   * reused, most notably the global Quick Add modal.
   */
  resetExplicitSelectionContract() {
    this.#abortController?.abort();
    this.#abortController = undefined;
    this.#pendingRequestUrl = undefined;
    this.#explicitSelectionOptionId = undefined;
    this.#requiresExplicitSelection = this.dataset.requireExplicitSelection === 'true';
    this.#refreshOptionCaches();

    if (this.#requiresExplicitSelection) this.#restoreExplicitSelection();
  }

  #refreshOptionCaches() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    this.#checkedIndices = [];
    this.#radios = [];

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset.querySelectorAll('input'));
      this.#radios.push(radios);
      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      this.#checkedIndices.push(initialCheckedIndex === -1 ? [] : [initialCheckedIndex]);
    });
  }

  /**
   * Returns the current product's purchase surfaces without touching cards or
   * other products that may exist in the same document.
   */
  #getPurchaseSurfaces() {
    const productId = this.dataset.productId;
    const scope = this.closest('.shopify-section, dialog, product-card');
    if (!productId || !scope) return {};

    const productForm = scope.querySelector(`product-form-component[data-product-id="${CSS.escape(productId)}"]`);
    const sticky = scope.querySelector(`sticky-add-to-cart[data-product-id="${CSS.escape(productId)}"]`);

    return {
      variantId: productForm?.querySelector('[ref="variantId"]'),
      mainButton: productForm?.querySelector('[ref="addToCartButton"]'),
      stickyButton: sticky?.querySelector('[ref="addToCartButton"]'),
      acceleratedCheckout: productForm?.querySelector('.accelerated-checkout-block'),
    };
  }

  /** @param {string} status */
  #showSelectionStatus(status) {
    const prompt = this.querySelector('[data-variant-selection-prompt]');
    if (!(prompt instanceof HTMLElement)) return;

    const message =
      status === 'loading'
        ? this.dataset.selectionLoading
        : status === 'error'
          ? this.dataset.selectionError
          : this.dataset.selectionPrompt;

    prompt.textContent = message ?? '';
    prompt.hidden = false;
    this.dataset.selectionState = status;
  }

  #lockPurchaseSurfaces() {
    const { variantId, mainButton, stickyButton, acceleratedCheckout } = this.#getPurchaseSurfaces();
    const buttons = [mainButton, stickyButton].filter((button) => button instanceof HTMLButtonElement);

    for (const button of buttons) {
      button.disabled = true;
      button.dataset.variantSelectionRequired = 'true';
      const label = button.querySelector('[data-add-to-cart-label]');
      if (label) label.textContent = this.dataset.selectionLabel ?? '';
    }

    if (acceleratedCheckout instanceof HTMLElement) {
      acceleratedCheckout.hidden = true;
      acceleratedCheckout.dataset.variantSelectionRequired = 'true';
    }

    if (variantId instanceof HTMLInputElement) {
      variantId.value = '';
      variantId.disabled = true;
      variantId.dataset.variantSelectionRequired = 'true';
    }
  }

  /** @param {boolean} [showError] */
  #restoreExplicitSelection(showError = false) {
    if (!this.#requiresExplicitSelection) return;

    this.#explicitSelectionOptionId = undefined;
    for (const field of this.querySelectorAll('[data-explicit-selection-field]')) {
      if (field instanceof HTMLFieldSetElement) {
        const fieldsetIndex = Number.parseInt(field.dataset.fieldsetIndex || '');
        const radios = Array.from(field.querySelectorAll('input'));
        for (const radio of radios) {
          radio.checked = false;
          radio.dataset.currentChecked = 'false';
          radio.dataset.previousChecked = 'false';
        }
        if (!Number.isNaN(fieldsetIndex)) {
          this.#radios[fieldsetIndex] = radios;
          this.#checkedIndices[fieldsetIndex] = [];
        }
      } else if (field instanceof HTMLSelectElement) {
        const placeholder = field.querySelector('option[value=""][disabled]');
        for (const option of field.options) option.removeAttribute('selected');
        if (placeholder instanceof HTMLOptionElement) {
          placeholder.selected = true;
          placeholder.setAttribute('selected', 'selected');
        } else {
          field.selectedIndex = -1;
        }
      }
    }

    this.#lockPurchaseSurfaces();
    this.#showSelectionStatus(showError ? 'error' : 'required');
  }

  #markExplicitSelectionPending() {
    if (this.dataset.requireExplicitSelection !== 'true') return;
    this.#lockPurchaseSurfaces();
    this.#showSelectionStatus('loading');
  }

  /** @param {object | null} resource @param {string} optionId */
  #completeExplicitSelection(resource, optionId) {
    if (!this.#requiresExplicitSelection && !this.#explicitSelectionOptionId) return;
    this.#requiresExplicitSelection = false;
    this.#explicitSelectionOptionId = optionId;
    this.dataset.selectionState = 'complete';

    const prompt = this.querySelector('[data-variant-selection-prompt]');
    if (prompt instanceof HTMLElement) prompt.hidden = true;

    this.#applyResolvedPurchaseState(resource);
  }

  /** @param {object | null} resource */
  #applyResolvedPurchaseState(resource) {
    const { variantId, mainButton, stickyButton, acceleratedCheckout } = this.#getPurchaseSurfaces();
    const available = resource?.available === true;
    const label = available
      ? this.dataset.addToCartLabel
      : resource
        ? this.dataset.soldOutLabel
        : this.dataset.unavailableLabel;

    for (const button of [mainButton, stickyButton]) {
      if (!(button instanceof HTMLButtonElement)) continue;
      button.disabled = !available;
      delete button.dataset.variantSelectionRequired;
      const labelElement = button.querySelector('[data-add-to-cart-label]');
      if (labelElement) labelElement.textContent = label ?? '';
    }

    if (variantId instanceof HTMLInputElement) {
      variantId.value = resource?.id?.toString() ?? '';
      variantId.disabled = !resource?.id;
      delete variantId.dataset.variantSelectionRequired;
    }

    if (acceleratedCheckout instanceof HTMLElement) {
      acceleratedCheckout.hidden = !available;
      delete acceleratedCheckout.dataset.variantSelectionRequired;
    }

    const stickyVariant = stickyButton?.closest('sticky-add-to-cart')?.querySelector('.sticky-add-to-cart__variant');
    if (stickyVariant && resource?.title) stickyVariant.textContent = resource.title;
  }

  /** @param {string} optionId */
  #reapplyExplicitSelection(optionId) {
    const field = this.querySelector('[data-explicit-selection-field]');
    if (!field) return false;

    const option = field.querySelector(`[data-option-value-id="${CSS.escape(optionId)}"]`);
    if (option instanceof HTMLInputElement) {
      this.updateSelectedOption(option);
      return true;
    }
    if (option instanceof HTMLOptionElement && option.parentElement instanceof HTMLSelectElement) {
      option.parentElement.value = option.value;
      this.updateSelectedOption(option.parentElement);
      return true;
    }
    return false;
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    const selectedOptionId = selectedOption.dataset.optionValueId;
    if (!selectedOptionId) return;

    this.updateSelectedOption(event.target);
    this.#markExplicitSelectionPending();
    this.dispatchEvent(new VariantSelectedEvent({
      id: selectedOption.dataset.optionValueId ?? '',
    }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    const explicitField = event.target.closest('[data-explicit-selection-field]');
    const explicitSelectionOptionId = explicitField ? selectedOptionId : this.#explicitSelectionOptionId;
    this.fetchUpdatedSection(
      this.buildRequestUrl(selectedOption),
      morphElementSelector,
      selectedOptionId,
      explicitSelectionOptionId,
      { isOnProductPage, newProductUrl: loadsNewProduct ? newUrl : undefined }
    );
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          if (checkedIndices[0] !== inputIndex) checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   * @param {string} selectedOptionId - The option value that initiated this request.
   * @param {string} [explicitSelectionOptionId] - The explicit size to preserve through the server morph.
   * @param {{isOnProductPage: boolean, newProductUrl?: string}} historyState - History state committed only after success.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector, selectedOptionId, explicitSelectionOptionId, historyState) {
    this.#abortController?.abort();
    const controller = new AbortController();
    const requestProductId = this.dataset.productId;
    this.#abortController = controller;

    fetch(requestUrl, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`Variant request failed with ${response.status}`);
        return response.text();
      })
      .then((responseText) => {
        if (
          controller !== this.#abortController ||
          controller.signal.aborted ||
          requestProductId !== this.dataset.productId
        ) return;
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) {
          if (this.dataset.requireExplicitSelection === 'true') {
            throw new Error('Selected variant response is missing product data');
          }
          return;
        }

        const resource = JSON.parse(textContent);
        let newProduct;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          const { overflowList } = this.refs;
          const wasSwatchesExpanded =
            overflowList instanceof OverflowList && overflowList.getAttribute('disabled') === 'true';

          newProduct = this.updateVariantPicker(html);

          if (wasSwatchesExpanded) {
            const overflowListAfterMorph = overflowList;
            if (overflowListAfterMorph instanceof OverflowList) {
              overflowListAfterMorph.showAll();
            }
          }
        }

        if (controller !== this.#abortController || controller.signal.aborted) return;
        const explicitSelectionRestored = explicitSelectionOptionId
          ? this.#reapplyExplicitSelection(explicitSelectionOptionId)
          : false;
        const requiresExplicitSelectionContract = this.dataset.requireExplicitSelection === 'true';

        // Dispatch for all paths so product-form-component can reset #variantChangeInProgress
        this.dispatchEvent(
          new VariantUpdateEvent(resource, selectedOptionId, {
            html,
            productId: this.dataset.productId ?? '',
            newProduct,
          })
        );

        if (requiresExplicitSelectionContract) {
          if (explicitSelectionOptionId && explicitSelectionRestored) {
            this.#completeExplicitSelection(resource, explicitSelectionOptionId);
          } else {
            this.#requiresExplicitSelection = true;
            this.#explicitSelectionOptionId = undefined;
            this.#restoreExplicitSelection();
          }
        }

        if (
          requiresExplicitSelectionContract &&
          historyState.newProductUrl &&
          !(explicitSelectionOptionId && explicitSelectionRestored)
        ) {
          this.#commitVariantHistory(null, historyState, controller);
        } else if (!requiresExplicitSelectionContract || (explicitSelectionOptionId && explicitSelectionRestored)) {
          this.#commitVariantHistory(resource, historyState, controller);
        }
      })
      .catch((error) => {
        if (
          error.name === 'AbortError' ||
          controller !== this.#abortController ||
          requestProductId !== this.dataset.productId
        ) return;

        console.error(error);
        this.dispatchEvent(
          new CustomEvent('variant-selection:error', {
            bubbles: true,
            detail: { productId: this.dataset.productId ?? '' },
          })
        );
        this.#requiresExplicitSelection = this.dataset.requireExplicitSelection === 'true';
        this.#restoreExplicitSelection(true);
      });
  }

  /**
   * @param {object | null} resource
   * @param {{isOnProductPage: boolean, newProductUrl?: string}} historyState
   * @param {AbortController} controller
   */
  #commitVariantHistory(resource, historyState, controller) {
    if (!historyState.isOnProductPage) return;
    const url = new URL(window.location.href);
    const variantId = resource?.id?.toString();

    if (variantId) url.searchParams.set('variant', variantId);
    else url.searchParams.delete('variant');
    if (historyState.newProductUrl) url.pathname = historyState.newProductUrl;
    if (url.href === window.location.href) return;

    yieldToMainThread().then(() => {
      if (!this.isConnected || controller !== this.#abortController || controller.signal.aborted) return;
      history.replaceState({}, '', url.toString());
    });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    this.#refreshOptionCaches();
    this.updateVariantPickerCss();

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector(
      'select option[selected][data-option-value-id], fieldset input:checked[data-option-value-id]'
    );

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(
      this.querySelectorAll(
        'select option[selected][data-option-value-id], fieldset input:checked[data-option-value-id]'
      )
    );

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
